import { useMemo } from "react";
import { X } from "lucide-react";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ReferenceLine, ResponsiveContainer, Legend } from "recharts";
import { kpisOf, dailySeries, mean, fmtN, fmtPct, C, SITE_COLORS } from "./ivUtils";

const tooltipStyle = { background: "rgb(var(--th-solid))", border: "1px solid rgb(var(--th-border) / 0.14)", borderRadius: 10, fontSize: 11, color: "rgb(var(--th-strong))" };
const RATING_COLORS = { Standard: C.green, "Non-Critical": C.yellow, Critical: C.red };

export default function IvAgentDrawer({ data, cisco, onClose }) {
  const agent = data.agents[cisco];
  const rows = useMemo(() => {
    const f = (feed) => data[feed].filter((r) => r.c === cisco);
    return { voc: f("voc"), answered: f("answered"), held: f("held"), hold: f("hold"), talk: f("talk"), aht: f("aht"),
             pmt: f("pmt").sort((a, b) => ((a.d || "") < (b.d || "") ? 1 : -1)), qsl: f("qsl").sort((a, b) => (a.d < b.d ? 1 : -1)) };
  }, [data, cisco]);
  const k = useMemo(() => kpisOf(rows), [rows]);
  const ro = data.roster[cisco];
  const cr = data.critical[cisco];
  const mdHist = useMemo(() => data.monthly.filter((m) => m.c === cisco).sort((a, b) => (a.m < b.m ? -1 : 1)), [data, cisco]);

  const callSeries = useMemo(() => {
    const by = {};
    rows.answered.forEach((r) => { (by[r.d] = by[r.d] || { d: r.d.slice(5) }).ans = r.v; });
    rows.held.forEach((r) => { (by[r.d] = by[r.d] || { d: r.d.slice(5) }).held = r.v; });
    return Object.keys(by).sort().map((d) => by[d]);
  }, [rows]);
  const timeSeries = useMemo(() => {
    const by = {};
    rows.aht.forEach((r) => { (by[r.d] = by[r.d] || { d: r.d.slice(5) }).aht = r.v; });
    rows.talk.forEach((r) => { (by[r.d] = by[r.d] || { d: r.d.slice(5) }).talk = r.v; });
    rows.hold.forEach((r) => { (by[r.d] = by[r.d] || { d: r.d.slice(5) }).hold = r.v; });
    return Object.keys(by).sort().map((d) => by[d]);
  }, [rows]);
  const vocSeries = useMemo(() => dailySeries(rows.voc, (rs) => {
    const n = rs.reduce((a, r) => a + r.n, 0);
    return n ? (100 * rs.reduce((a, r) => a + r.t2b, 0)) / n : null;
  }).map((p) => ({ d: p.d.slice(5), v: p.v })), [rows.voc]);

  const chips = [
    { label: "VOC", v: fmtPct(k.voc) }, { label: "Surveys", v: k.surveys || "—" },
    { label: "Answered", v: fmtN(k.answered, 0) }, { label: "Held %", v: fmtPct(k.heldPct) },
    { label: "Hold", v: k.hold != null ? `${fmtN(k.hold, 0)}s` : "—" }, { label: "Talk", v: k.talk != null ? `${fmtN(k.talk, 0)}s` : "—" },
    { label: "AHT", v: k.aht != null ? `${fmtN(k.aht, 0)}s` : "—" },
    { label: "PMT", v: fmtPct(k.pmt) }, { label: "QSL", v: fmtPct(k.qsl) },
    { label: "Fatals", v: cr?.fatal ?? "—" }, { label: "Adherence", v: fmtPct(ro?.adh ?? mdHist.slice(-1)[0]?.adh) },
  ];

  if (!agent) return null;
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="iv-drawer">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative flex h-full w-full max-w-2xl flex-col overflow-y-auto border-l border-thborder/15 bg-appbg shadow-2xl">
        <div className="sticky top-0 z-10 border-b border-thborder/10 bg-appbg/95 px-6 py-4 backdrop-blur">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="font-heading text-xl font-bold text-thstrong">{agent.name || `Agent ${cisco}`}</h2>
              <p className="font-mono-hud mt-1 text-[10px] uppercase tracking-[0.2em] text-thdim">
                Cisco {cisco} · <span style={{ color: SITE_COLORS[agent.site] }}>{agent.site}</span> · {agent.tl || agent.team || "—"}
              </p>
              <p className="font-mono-hud mt-0.5 text-[9px] uppercase tracking-[0.15em] text-thdim">Full history shown (not limited by the date filter)</p>
            </div>
            <button data-testid="iv-drawer-close" onClick={onClose} className="rounded-md p-2 text-thmute hover:bg-thhover/10 hover:text-thstrong"><X className="h-5 w-5" /></button>
          </div>
        </div>

        <div className="space-y-6 px-6 py-5">
          <div className="flex flex-wrap gap-2">
            {chips.map((c) => (
              <div key={c.label} className="rounded-xl border border-thborder/10 bg-thinput px-3 py-2">
                <p className="font-mono-hud text-[9px] uppercase tracking-[0.12em] text-thdim">{c.label}</p>
                <p className="font-mono-hud text-sm font-semibold text-thcyan">{c.v}</p>
              </div>
            ))}
          </div>

          {callSeries.length > 1 && (
            <div className="mk-card p-4">
              <p className="font-mono-hud mb-2 text-[10px] uppercase tracking-[0.2em] text-thdim">Daily answered & held</p>
              <div className="h-40">
                <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={100} initialDimension={{ width: 1, height: 1 }}>
                  <BarChart data={callSeries} margin={{ top: 4, right: 8, bottom: 0, left: -14 }}>
                    <XAxis dataKey="d" tick={{ fill: "#7a8fb5", fontSize: 9, fontFamily: "JetBrains Mono" }} interval="preserveStartEnd" />
                    <YAxis tick={{ fill: "#7a8fb5", fontSize: 9, fontFamily: "JetBrains Mono" }} />
                    <Tooltip contentStyle={tooltipStyle} labelStyle={{ color: "rgb(var(--th-mute))" }} />
                    <Legend wrapperStyle={{ fontSize: 10 }} />
                    <Bar dataKey="ans" fill={C.blue} radius={[2, 2, 0, 0]} name="Answered" />
                    <Bar dataKey="held" fill={C.yellow} radius={[2, 2, 0, 0]} name="Held" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {timeSeries.length > 1 && (
            <div className="mk-card p-4">
              <p className="font-mono-hud mb-2 text-[10px] uppercase tracking-[0.2em] text-thdim">Daily AHT / Talk / Hold (sec) · dashed = AHT target {data.targets.aht}s</p>
              <div className="h-44">
                <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={100} initialDimension={{ width: 1, height: 1 }}>
                  <LineChart data={timeSeries} margin={{ top: 4, right: 8, bottom: 0, left: -14 }}>
                    <XAxis dataKey="d" tick={{ fill: "#7a8fb5", fontSize: 9, fontFamily: "JetBrains Mono" }} interval="preserveStartEnd" />
                    <YAxis tick={{ fill: "#7a8fb5", fontSize: 9, fontFamily: "JetBrains Mono" }} />
                    <Tooltip contentStyle={tooltipStyle} labelStyle={{ color: "rgb(var(--th-mute))" }} />
                    <Legend wrapperStyle={{ fontSize: 10 }} />
                    <ReferenceLine y={data.targets.aht} stroke={C.yellow} strokeDasharray="5 4" strokeOpacity={0.7} />
                    <Line type="monotone" dataKey="aht" stroke={C.blue} strokeWidth={2} dot={false} connectNulls name="AHT" />
                    <Line type="monotone" dataKey="talk" stroke={C.green} strokeWidth={1.5} dot={false} connectNulls name="Talk" />
                    <Line type="monotone" dataKey="hold" stroke={C.purple} strokeWidth={1.5} dot={false} connectNulls name="Hold" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {vocSeries.length > 0 && (
            <div className="mk-card p-4">
              <p className="font-mono-hud mb-2 text-[10px] uppercase tracking-[0.2em] text-thdim">Daily VOC Top-2-Box %</p>
              <div className="h-36">
                <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={100} initialDimension={{ width: 1, height: 1 }}>
                  <BarChart data={vocSeries} margin={{ top: 4, right: 8, bottom: 0, left: -14 }}>
                    <XAxis dataKey="d" tick={{ fill: "#7a8fb5", fontSize: 9, fontFamily: "JetBrains Mono" }} interval="preserveStartEnd" />
                    <YAxis tick={{ fill: "#7a8fb5", fontSize: 9, fontFamily: "JetBrains Mono" }} domain={[0, 100]} />
                    <Tooltip contentStyle={tooltipStyle} labelStyle={{ color: "rgb(var(--th-mute))" }} />
                    <Bar dataKey="v" fill={C.green} radius={[2, 2, 0, 0]} name="Top-2-Box %" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {mdHist.length > 0 && (
            <div>
              <p className="font-mono-hud mb-2 text-[10px] uppercase tracking-[0.2em] text-thdim">Monthly history (Monthly_Dump)</p>
              <div className="flex flex-wrap gap-2">
                {mdHist.map((m) => (
                  <div key={m.m} className="rounded-xl border border-thborder/10 bg-thinput px-3 py-2">
                    <p className="font-mono-hud text-[9px] uppercase text-thdim">{m.m}</p>
                    <p className="font-mono-hud text-xs font-semibold text-thbody">VOC {fmtPct(m.voc)} · Q {fmtPct(m.q)} · Adh {fmtPct(m.adh)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <p className="font-mono-hud mb-2 text-[10px] uppercase tracking-[0.2em] text-thdim">PMT Evaluations ({rows.pmt.length})</p>
            {rows.pmt.length === 0 ? <p className="text-xs text-thdim">No internal evaluations loaded yet.</p> : (
              <div className="space-y-2">
                {rows.pmt.map((p, i) => (
                  <div key={i} className="rounded-xl border border-thborder/10 bg-thinput p-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="font-mono-hud text-xs text-thmute">{p.d || "—"} · {p.skill}</p>
                      <div className="flex items-center gap-2">
                        <span className="font-mono-hud text-sm font-semibold" style={{ color: p.score != null && p.score >= data.targets.pmt ? C.green : C.red }}>{fmtPct(p.score)}</span>
                        <span className="rounded-full px-2 py-0.5 font-mono-hud text-[9px] uppercase" style={{ background: `${RATING_COLORS[p.rating] || C.grey}22`, color: RATING_COLORS[p.rating] || C.grey }}>{p.rating}</span>
                      </div>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {data.pmt_sub_names.map((name, j) => {
                        const v = p.subs[j];
                        return (
                          <span key={j} title={name} className={`rounded px-1.5 py-0.5 font-mono-hud text-[9px] ${v == null ? "bg-thhover/10 text-thdim" : v ? "bg-[#10B981]/15 text-[#10B981]" : "bg-[#F43F5E]/15 text-[#F43F5E]"}`}>
                            {name.length > 22 ? `${name.slice(0, 20)}…` : name}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="pb-6">
            <p className="font-mono-hud mb-2 text-[10px] uppercase tracking-[0.2em] text-thdim">QSL Audits ({rows.qsl.length})</p>
            {rows.qsl.length === 0 ? <p className="text-xs text-thdim">No external audits.</p> : (
              <div className="max-h-72 space-y-1.5 overflow-auto">
                {rows.qsl.map((q, i) => (
                  <div key={i} className="flex items-center justify-between rounded-xl border border-thborder/10 bg-thinput px-3 py-2">
                    <p className="font-mono-hud text-xs text-thmute">{q.d}</p>
                    <p className="font-mono-hud text-sm font-semibold" style={{ color: q.score >= data.targets.qsl ? C.green : q.score > 0 ? C.yellow : C.red }}>{fmtN(q.score, 0)}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
